import { Suspense } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import League, { LeagueProvider } from "@leagueplatform/core";
import { LoadingIndicator } from "@leagueplatform/web-common-components";
import { initMasonryRegistry as initBenefitsMasonryRegistry } from "@leagueplatform/benefits";
import { ToastManager } from "@leagueplatform/toast-messages";
import { RewardsEventController } from "@leagueplatform/rewards-events";
import "@leagueplatform/claims-ui";
import "@leagueplatform/health-journey";
import { QueryClientProviderWrapper } from "client-provider-wrapper";
import { OnlineStatusGate } from "components/online-status-gate/online-status-gate.component";
import { UserAchievementsOrdering } from "components/user-achievements-ordering/user-achievements-ordering.component";
import { config, routesConfig } from "config";
import { appRoutes } from "app-routes";
import { useBfcacheReload } from "hooks/use-bf-cache-reload/use-bf-cache-reload.hook";
import { registerNormalizingDeeplinkHandler } from "masonry/normalize-deeplink-urls";
import { registerEmblemMasonryActions } from "masonry/register-emblem-actions";
import { registerEmblemCarePlansRenderers } from "masonry/register-emblem-care-plans-renderers";

import { registerCodeLookupRenderer } from "masonry/code-lookup/code-lookup-renderer";

League.overwriteRoutePaths(routesConfig);
// Registers Masonry node renderers used by the benefits tree — without
// this the recognised node types have no client-side renderer and the
// page renders blank.
initBenefitsMasonryRegistry();
registerNormalizingDeeplinkHandler();
// Emblem's tenant Masonry action handlers (openLiveChat,
// submitFormWithToastAndModal, dropNavigation deeplink) + the
// checkboxGroup renderer override. Registered once at bootstrap so
// they exist before any Masonry surface renders — the Home + Journey
// › Explore Ask-Eva tiles fire `openLiveChat` on tap, and Home does
// not import any page that would register these on demand.
registerEmblemMasonryActions();
// Overrides the SDK's default verticalLayout renderer globally (see
// register-emblem-care-plans-renderers.tsx), so — like the registrations
// above — it must be active before any Masonry surface renders rather
// than lazily on first visit to Care Plans, otherwise the override would
// flip on mid-session depending on navigation order.
registerEmblemCarePlansRenderers();
registerCodeLookupRenderer();

// One-shot cleanup of orphaned localStorage keys from before P3 (server-side FTUX).
// Safe to remove this block after a release or two — by then any active browser
// will have re-loaded the app and the keys will be gone.
if (typeof window !== "undefined") {
  window.localStorage.removeItem("emblem.ftuxComplete");
  window.localStorage.removeItem("emblem.featureHighlightsSeen");
  window.localStorage.removeItem("emblem.devForceOnboarding");
}

export const LeagueProviderWrapper = () => (
  <QueryClientProviderWrapper>
    <LeagueProvider config={config}>
      <UserAchievementsOrdering />
      <Suspense fallback={<LoadingIndicator />}>
        <OnlineStatusGate />
      </Suspense>
      <ToastManager />
      <RewardsEventController />
    </LeagueProvider>
  </QueryClientProviderWrapper>
);

const router = createBrowserRouter([
  {
    element: <LeagueProviderWrapper />,
    children: [
      {
        element: <Outlet />,
        children: appRoutes,
      },
    ],
  },
]);

export const App = () => {
  useBfcacheReload();
  return <RouterProvider router={router} />;
};
